import { Injectable } from '@angular/core';
import { ApiService } from '../../shared/services/core/api.service';
import { GiftCodeModel } from '../models/giftcode/gift-code.model';
import { Observable } from 'rxjs';
import { ErrorStateService } from '../../shared/services/core/error.service';
import { GiftCodeType } from '../models/giftcode/gift-code-type.model';
import { GiftCodeEntitlementValueType } from '../models/giftcode/gift-code-entitlement-value-type.model';
import { AuthService } from '../../user/services/auth.service';
import { MemberQuery } from '../../account/member/member.query';

@Injectable({
  providedIn: 'root',
})
export class GiftCodeService {
  constructor(private apiService: ApiService, private errorService: ErrorStateService, private authService: AuthService, private memberQuery: MemberQuery) {}

  /**
   * Validate GiftCode for Portal user
   * @param code GiftCode code to validate
   */
  validateGiftCode(code: string): Observable<GiftCodeModel> {
    let portalId = '';
    if(this.authService.isAuthenticated()){
      portalId = this.memberQuery.getUserContext().portalId;
    }
    return this.apiService.post(`giftCodes/apply?giftCode=${code}&portalId=${portalId}`, {});
  }

  /**
   * Calculates the decuation to be applied to the basket when a GiftCode is used
   * @param giftCode - GiftCode being applied
   * @param productPrice - The price of the products being purchased 
   * @param shippingPrice - The shipping price for the order
   * @param totalBasketPrice - The total basket price (inc. tax)
   */
  calculateGiftCodeDeduction(
    giftCode: GiftCodeModel,
    productPrice: number,
    shippingPrice: number,
    totalBasketPrice: number
  ): number {
    this.errorService.clearErrors();
    let discount = 0;

    if (giftCode.GiftCodeAssignType === GiftCodeType.Order) {
      discount = this._getDiscountByEntitlementType(giftCode.EntitlementValueType, giftCode.EntitlementValue, totalBasketPrice);
    } else if (giftCode.GiftCodeAssignType === GiftCodeType.Shipping) {
      if (shippingPrice === 0) {
        this.errorService.addError('Gift Code cannot be applied if the shipping is already 0');
        return 0;
      }

      discount = this._getDiscountByEntitlementType(
        giftCode.EntitlementValueType,
        giftCode.EntitlementValue,
        totalBasketPrice,
        shippingPrice
      );
    }

    return Math.max(0, totalBasketPrice - discount);
  }

  /**
   * Calculates the basket discount using the entitlementType
   * @param entitlementType - How the GiftCode discounts are applied e.g. by percentage
   * @param entitlementValue - The value associated with the entitlementType - e.g entitlementValue = 70 with entitlementType = Percentage means 70% discount
   * @param totalBasketPrice - Total order price 
   * @param amountToDiscountFrom - How much of the order is able to be discounted
   */
  private _getDiscountByEntitlementType(
    entitlementType: GiftCodeEntitlementValueType,
    entitlementValue: number,
    totalBasketPrice: number,
    amountToDiscountFrom: number = 0
  ) {
    let discount = 0;

    switch (entitlementType) {
      // Value
      case GiftCodeEntitlementValueType.Value: {
        discount = amountToDiscountFrom > 0 ? amountToDiscountFrom - entitlementValue : entitlementValue;
        break;
      }
      case GiftCodeEntitlementValueType.Percentage: {
        // Percentage
        let percentageAmount = (entitlementValue / 100) * this._getAmountToDiscount(amountToDiscountFrom, totalBasketPrice);
        discount = amountToDiscountFrom > 0 ? amountToDiscountFrom - percentageAmount : percentageAmount;
        break;
      }
      case GiftCodeEntitlementValueType.NumberOfEntitlements: {
        // Number of Items - Only used for Free
        discount = this._getAmountToDiscount(amountToDiscountFrom, totalBasketPrice);
        break;
      }
      default: {
        this.errorService.addError('Invalid Gift Code');
        break;
      }
    }

    return discount;
  }

  private _getAmountToDiscount(amount: number, totalBasketPrice: number) {
    return amount > 0 ? amount : totalBasketPrice;
  }
}
